// The live clock hook for operational screens. The pure helpers it pairs
// with (parse / elapsed / tone / format) live in `time.ts`.

import { useEffect, useState } from 'react'

export * from './time'

// ── One shared ticker ────────────────────────────────────────────────
// A board of 60 tickets must not start 60 intervals: every subscriber of the
// same period shares one timer, and the timer stops when the last one leaves.

type Listener = (now: number) => void
const tickers = new Map<number, { listeners: Set<Listener>; handle: ReturnType<typeof setInterval> }>()

function subscribe(periodMs: number, fn: Listener): () => void {
  let t = tickers.get(periodMs)
  if (!t) {
    const listeners = new Set<Listener>()
    const handle = setInterval(() => {
      const now = Date.now()
      listeners.forEach((l) => { try { l(now) } catch { /* one listener never stops the rest */ } })
    }, periodMs)
    t = { listeners, handle }
    tickers.set(periodMs, t)
  }
  t.listeners.add(fn)
  return () => {
    const cur = tickers.get(periodMs)
    if (!cur) return
    cur.listeners.delete(fn)
    if (cur.listeners.size === 0) {
      clearInterval(cur.handle)
      tickers.delete(periodMs)
    }
  }
}

/** The current time, re-rendering every `periodMs` (0 / false = never: the
 *  caller does not need a live clock). */
export function useNow(periodMs: number | false = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!periodMs || periodMs <= 0) return undefined
    setNow(Date.now())
    return subscribe(periodMs, setNow)
  }, [periodMs])
  return now
}

